'use client';

import { useEffect, useRef, useState } from 'react';
import Image from 'next/image';
import { useTranslations } from 'next-intl';
import { Link, usePathname } from '@/i18n/navigation';
import { LocaleSwitcher } from './LocaleSwitcher';
import { MobileNav } from './MobileNav';
import { AnimatePresence, motion } from 'motion/react';
import { localized, type NavigationItemConfig, type RestaurantLocale } from '@restaurant/config';
import styles from './PublicNavbar.module.css';

const MENU_CATEGORIES = [
  { id: 'menu-du-jour', slug: 'menu-du-jour' },
  { id: 'entrees', slug: 'entrees' },
  { id: 'plats', slug: 'plats' },
  { id: 'desserts', slug: 'desserts' },
  { id: 'boissons', slug: 'boissons' },
] as const;

interface PublicNavbarProps {
  navigation: NavigationItemConfig[];
  locale: RestaurantLocale;
}

export function PublicNavbar({ navigation, locale }: PublicNavbarProps) {
  const t = useTranslations('common');
  const pathname = usePathname();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const closeTimeout = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 48);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  function openMenu() {
    clearTimeout(closeTimeout.current);
    setMenuOpen(true);
  }

  function scheduleClose() {
    closeTimeout.current = setTimeout(() => setMenuOpen(false), 120);
  }

  const isMenuActive = pathname === '/menu' || pathname.startsWith('/menu/');
  const activeCategory = pathname.startsWith('/menu/')
    ? pathname.split('/')[2]
    : null;

  function isActive(href: string) {
    if (href === '/menu') return isMenuActive;
    return pathname === href || pathname.startsWith(`${href}/`);
  }

  const visibleLinks = navigation.filter((item) => item.isActive && item.showInNav);
  const hasTakeaway = visibleLinks.some((item) => item.key === 'takeaway');
  const otherLinks = visibleLinks.filter((item) => !['home', 'menu', 'takeaway'].includes(item.key));

  const isHome = pathname === '/';

  return (
    <header className={`${styles.header} ${scrolled ? styles.scrolled : ''}`}>
      <div className={styles.inner}>
        {/* Logo */}
        <Link href="/" className={styles.logo} aria-label={t('siteName')}>
          <Image
            src="/logo_Site_remove.webp"
            alt={t('siteName')}
            width={500}
            height={500}
            className={styles.logoImg}
            sizes="56px"
            priority
          />
        </Link>

        {/* Desktop nav */}
        <nav className={styles.links} aria-label={t('mainNavigation')}>
          {/* Accueil */}
          <Link
            href="/"
            className={`${styles.link} ${isHome ? styles.linkActive : ''}`}
          >
            {t('home')}
          </Link>

          {/* Menu with dropdown */}
          <div
            className={styles.menuWrapper}
            onMouseEnter={openMenu}
            onMouseLeave={scheduleClose}
          >
            <Link
              href="/menu"
              className={`${styles.link} ${isMenuActive ? styles.linkActive : ''} ${styles.menuTrigger}`}
              aria-haspopup="true"
              aria-expanded={menuOpen}
            >
              {t('menu')}
              <span
                className={`${styles.triggerChevron} ${menuOpen ? styles.triggerChevronUp : ''}`}
                aria-hidden
              >
                <svg width="9" height="9" viewBox="0 0 9 9" fill="none">
                  <path
                    d="M1.5 3L4.5 6L7.5 3"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
            </Link>

            <AnimatePresence>
              {menuOpen && (
                <motion.div
                  className={styles.dropdown}
                  role="menu"
                  initial={{ opacity: 0, y: -6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -6 }}
                  transition={{ duration: 0.15, ease: [0.16, 1, 0.3, 1] }}
                  onMouseEnter={openMenu}
                  onMouseLeave={scheduleClose}
                >
                  <ul className={styles.dropdownList}>
                    {MENU_CATEGORIES.map(({ id, slug }) => {
                      const active = activeCategory === slug;
                      return (
                        <li key={id}>
                          <Link
                            href={{ pathname: '/menu/[category]', params: { category: slug } }}
                            className={`${styles.dropdownItem} ${active ? styles.dropdownItemActive : ''}`}
                            role="menuitem"
                            onClick={() => setMenuOpen(false)}
                          >
                            <span className={`${styles.dropdownDot} ${active ? styles.dropdownDotActive : ''}`} aria-hidden />
                            {t(`menuCategories.${id}`)}
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                  <div className={styles.dropdownDivider} />
                  <Link
                    href="/menu"
                    className={styles.dropdownAll}
                    role="menuitem"
                    onClick={() => setMenuOpen(false)}
                  >
                    {t('menuCategories.viewAll')}
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden>
                      <path
                        d="M1 6h10M6 1l5 5-5 5"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </Link>
                  {hasTakeaway && (
                    <Link
                      href="/commande-a-emporter"
                      className={styles.dropdownAll}
                      role="menuitem"
                      onClick={() => setMenuOpen(false)}
                    >
                      {t('takeawayLong')}
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden>
                        <path
                          d="M1 6h10M6 1l5 5-5 5"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </Link>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Other links */}
          {otherLinks.map((item) => (
            <Link
              key={item.key}
              href={item.href as any}
              className={`${styles.link} ${isActive(item.href) ? styles.linkActive : ''}`}
            >
              {localized(item.labels, locale)}
            </Link>
          ))}
        </nav>

        {/* Desktop actions */}
        <div className={styles.actions}>
          <LocaleSwitcher />
          {hasTakeaway && <Link href="/commande-a-emporter" className={styles.cta}>
            <span>{t('order')}</span>
            <svg width="13" height="13" viewBox="0 0 13 13" fill="none" aria-hidden>
              <path
                d="M1 6.5h11M6.5 1l5.5 5.5-5.5 5.5"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </Link>}
        </div>

        {/* Mobile hamburger */}
        <MobileNav navigation={navigation} locale={locale} />
      </div>
    </header>
  );
}
